import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Filter,
  Calendar,
  Building2,
  Search,
  Eye,
  BarChart3,
  PieChart,
  FileSpreadsheet,
  FileImage
} from 'lucide-react';
import { useExport } from '../hooks/useExport';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from './ui/table';
import { Separator } from './ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportsPage = () => {
  const navigate = useNavigate();
  const { exportToExcel, exportToPDF, exportToCSV, exporting } = useExport();
  const [activeReport, setActiveReport] = useState('complete');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    empresa: '',
    nome: '',
    filiais: '',
    cargo: '',
    admissao_inicio: '',
    admissao_fim: '',
    regime: '',
    departamento: '',
    situacao: ''
  });

  // Options for filters
  const [options, setOptions] = useState({
    companies: [],
    branches: [],
    positions: [],
    departments: [],
    status: []
  });

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const [companies, branches, positions, departments, status] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/branches`),
        axios.get(`${API}/positions`),
        axios.get(`${API}/departments`),
        axios.get(`${API}/status`)
      ]);

      setOptions({
        companies: companies.data,
        branches: branches.data,
        positions: positions.data,
        departments: departments.data,
        status: status.data
      });
    } catch (error) {
      console.error('Erro ao carregar opções:', error);
    }
  };

  const generateReport = async () => {
    try {
      setLoading(true);
      let endpoint = '';
      let params = {};

      if (activeReport === 'complete') {
        endpoint = '/reports/complete';
        // Convert __all__ values to empty strings for API
        params = Object.fromEntries(
          Object.entries(filters).map(([key, value]) => [key, value === '__all__' ? '' : value])
        );
      } else if (activeReport === 'company-totals') {
        endpoint = '/reports/company-totals';
        params = { empresa: filters.empresa === '__all__' ? '' : filters.empresa };
      } else if (activeReport === 'consultas') {
        endpoint = '/employees-detailed';
        params = Object.fromEntries(
          Object.entries(filters).map(([key, value]) => [key, value === '__all__' ? '' : value])
        );
      } else if (activeReport === 'position-salaries') {
        endpoint = '/position-salaries';
        params = {}; // No filters for position-salaries for now
      } else if (activeReport === 'charts') {
        // Navigate to charts report page
        navigate('/relatorios/graficos');
        return;
      }

      const response = await axios.get(`${API}${endpoint}`, { params });
      setReportData(response.data);
      
      if (response.data.length === 0) {
        toast.info('Nenhum dado encontrado com os filtros aplicados');
      }
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      empresa: '',
      nome: '',
      filiais: '',
      cargo: '',
      admissao_inicio: '',
      admissao_fim: '',
      regime: '',
      departamento: '',
      situacao: ''
    });
    setReportData([]);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const handleExportCSV = () => {
    if (reportData.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const filename = `relatorio_gridprofile_${activeReport}_${new Date().toISOString().split('T')[0]}`;
    
    if (activeReport === 'complete') {
      const headers = [
        'Nome', 'Empresa', 'Filiais', 'Cargo', 'Admissão', 'Data de Demissão', 'Regime', 'Departamento', 'Situação', 'Salário',
        'Acúmulo Função', 'VT', 'Desconto VT', 'VT Custo', 'VA', 'VR', 'Aux. Combustível', 'Auxílio Creche',
        '2ª Refeição', 'Pernoite', 'Assist. Médica', 'Odonto', 'Seg. Vida', 'Total Pass', '13º NF',
        'Med. Ocupacional', 'Prov. 13º', 'Prov. Férias', 'FGTS', 'INSS', 'TOTAL'
      ];
      const formattedData = reportData.map(row => ({
        nome: row.nome,
        empresa: row.empresa,
        filiais: row.filiais || '',
        cargo: row.cargo || '',
        admissao: formatDate(row.admissao),
        data_demissao: row.data_demissao ? formatDate(row.data_demissao) : '',
        regime: row.regime,
        departamento: row.departamento || '',
        situacao: row.situacao || '',
        salario: row.salario,
        acumulo_funcao: row.acumulo_funcao,
        vt: row.vt,
        desconto_vt: row.desconto_vt,
        vt_custo: row.vt_custo,
        va: row.va,
        vr: row.vr,
        aux_combustivel: row.aux_combustivel,
        auxilio_creche: row.auxilio_creche,
        segunda_refeicao: row.segunda_refeicao,
        pernoite: row.pernoite,
        assist_medica: row.assist_medica,
        odonto: row.odonto,
        seg_vida: row.seg_vida,
        total_pass: row.total_pass,
        nf_13: row.nf_13,
        med_ocupacional: row.med_ocupacional,
        prov_13: row.prov_13,
        prov_ferias: row.prov_ferias,
        fgts: row.fgts,
        inss: row.inss,
        total: row.total
      }));
      exportToCSV(formattedData, filename, headers);
    } else if (activeReport === 'company-totals') {
      const headers = ['Empresa', 'Total'];
      exportToCSV(reportData, filename, headers);
    } else if (activeReport === 'consultas') {
      const headers = [
        'Nome Completo', 'CPF', 'Data de Nascimento', 'Empresa', 'Filial', 'Cargo', 'Faixa',
        'Banco', 'Agência', 'Conta', 'Tipo de Conta', 'Chave PIX'
      ];
      const formattedData = reportData.map(row => ({
        nome_completo: row.nome,
        cpf: row.cpf,
        data_nascimento: formatDate(row.data_nascimento),
        empresa: row.empresa,
        filial: row.filiais || '',
        cargo: row.cargo || '',
        faixa: row.faixa || '',
        banco: row.banco || '',
        agencia: row.agencia || '',
        conta: row.conta || '',
        tipo_conta: row.tipo_conta || '',
        chave_pix: row.pix || ''
      }));
      exportToCSV(formattedData, filename, headers);
    }
  };

  const handleExportExcel = () => {
    if (reportData.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const filename = `relatorio_gridprofile_${activeReport}_${new Date().toISOString().split('T')[0]}`;
    
    if (activeReport === 'complete') {
      const formattedData = reportData.map(row => ({
        'Nome': row.nome,
        'Empresa': row.empresa,
        'Filiais': row.filiais || '',
        'Cargo': row.cargo || '',
        'Admissão': formatDate(row.admissao),
        'Data de Demissão': row.data_demissao ? formatDate(row.data_demissao) : '',
        'Regime': row.regime,
        'Departamento': row.departamento || '',
        'Situação': row.situacao || '',
        'Salário': row.salario,
        'Acúmulo Função': row.acumulo_funcao,
        'VT': row.vt,
        'Desconto VT': row.desconto_vt,
        'VT Custo': row.vt_custo,
        'VA': row.va,
        'VR': row.vr,
        'Aux. Combustível': row.aux_combustivel,
        'Auxílio Creche': row.auxilio_creche,
        '2ª Refeição': row.segunda_refeicao,
        'Pernoite': row.pernoite,
        'Assist. Médica': row.assist_medica,
        'Odonto': row.odonto,
        'Seg. Vida': row.seg_vida,
        'Total Pass': row.total_pass,
        '13º NF': row.nf_13,
        'Med. Ocupacional': row.med_ocupacional,
        'Prov. 13º': row.prov_13,
        'Prov. Férias': row.prov_ferias,
        'FGTS': row.fgts,
        'INSS': row.inss,
        'TOTAL': row.total
      }));
      exportToExcel(formattedData, filename, 'Relatório Completo');
    } else if (activeReport === 'company-totals') {
      const formattedData = reportData.map(row => ({
        'Empresa': row.empresa,
        'Total': row.total
      }));
      exportToExcel(formattedData, filename, 'Totais por Empresa');
    } else if (activeReport === 'consultas') {
      const formattedData = reportData.map(row => ({
        'Nome Completo': row.nome || '',
        'CPF': row.cpf || '',
        'Data de Nascimento': formatDate(row.data_nascimento),
        'Empresa': row.empresa || '',
        'Filial': row.filiais || '',
        'Cargo': row.cargo || '',
        'Faixa': row.faixa || '',
        'Banco': row.banco || '',
        'Agência': row.agencia || '',
        'Conta': row.conta || '',
        'Tipo de Conta': row.tipo_conta || '',
        'Chave PIX': row.pix || ''
      }));
      exportToExcel(formattedData, filename, 'Relatório de Consultas');
    } else if (activeReport === 'position-salaries') {
      const formattedData = reportData.map(row => ({
        'Empresa': row.empresa,
        'Cargo': row.cargo,
        'Departamento': row.departamento || '',
        'Modalidade': row.modalidade,
        'Júnior I': row.salario_junior_i,
        'Júnior II': row.salario_junior_ii,
        'Júnior III': row.salario_junior_iii,
        'Pleno I': row.salario_pleno_i,
        'Pleno II': row.salario_pleno_ii,
        'Pleno III': row.salario_pleno_iii,
        'Sênior I': row.salario_senior_i,
        'Sênior II': row.salario_senior_ii,
        'Sênior III': row.salario_senior_iii
      }));
      exportToExcel(formattedData, filename, 'Cargos e Salários');
    }
  };

  const handleExportPDF = () => {
    if (reportData.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const filename = `relatorio_gridprofile_${activeReport}_${new Date().toISOString().split('T')[0]}`;
    
    if (activeReport === 'complete') {
      const columns = [
        { header: 'Nome', dataKey: 'nome' },
        { header: 'Empresa', dataKey: 'empresa' },
        { header: 'Cargo', dataKey: 'cargo' },
        { header: 'Admissão', dataKey: 'admissao' },
        { header: 'Data de Demissão', dataKey: 'data_demissao' },
        { header: 'Regime', dataKey: 'regime' },
        { header: 'Salário', dataKey: 'salario' },
        { header: 'VT', dataKey: 'vt' },
        { header: 'VA', dataKey: 'va' },
        { header: 'VR', dataKey: 'vr' },
        { header: 'FGTS', dataKey: 'fgts' },
        { header: 'INSS', dataKey: 'inss' },
        { header: 'TOTAL', dataKey: 'total' }
      ];
      
      const formattedData = reportData.map(row => ({
        nome: row.nome,
        empresa: row.empresa,
        cargo: row.cargo || '',
        admissao: formatDate(row.admissao),
        data_demissao: row.data_demissao ? formatDate(row.data_demissao) : '',
        regime: row.regime,
        salario: row.salario,
        vt: row.vt,
        va: row.va,
        vr: row.vr,
        fgts: row.fgts,
        inss: row.inss,
        total: row.total
      }));
      
      exportToPDF(formattedData, filename, 'Relatório Completo de Funcionários', columns);
    } else if (activeReport === 'company-totals') {
      const columns = [
        { header: 'Empresa', dataKey: 'empresa' },
        { header: 'Total', dataKey: 'total' }
      ];
      
      exportToPDF(reportData, filename, 'Totais por Empresa', columns);
    } else if (activeReport === 'position-salaries') {
      const columns = [
        { header: 'Empresa', dataKey: 'empresa' },
        { header: 'Cargo', dataKey: 'cargo' },
        { header: 'Departamento', dataKey: 'departamento' },
        { header: 'Modalidade', dataKey: 'modalidade' },
        { header: 'Júnior I', dataKey: 'salario_junior_i' },
        { header: 'Júnior II', dataKey: 'salario_junior_ii' },
        { header: 'Júnior III', dataKey: 'salario_junior_iii' },
        { header: 'Pleno I', dataKey: 'salario_pleno_i' },
        { header: 'Pleno II', dataKey: 'salario_pleno_ii' },
        { header: 'Pleno III', dataKey: 'salario_pleno_iii' },
        { header: 'Sênior I', dataKey: 'salario_senior_i' },
        { header: 'Sênior II', dataKey: 'salario_senior_ii' },
        { header: 'Sênior III', dataKey: 'salario_senior_iii' }
      ];
      
      exportToPDF(reportData, filename, 'Relatório de Cargos e Salários', columns);
    }
  };

  const reports = [
    {
      key: 'complete',
      title: 'Relatório Completo',
      description: 'Relatório com todas as informações dos funcionários',
      icon: FileText
    },
    {
      key: 'company-totals',
      title: 'Totais por Empresa',
      description: 'Relatório com totais agrupados por empresa',
      icon: BarChart3
    },
    {
      key: 'consultas',
      title: 'Relatório de Consultas',
      description: 'Relatório com dados básicos e bancários dos funcionários',
      icon: Search
    },
    {
      key: 'position-salaries',
      title: 'Cargos e Salários',
      description: 'Relatório completo de cargos e suas faixas salariais',
      icon: FileSpreadsheet
    },
    {
      key: 'charts',
      title: 'Relatório de Gráficos',
      description: 'Visualização gráfica dos dados em formato de gráficos',
      icon: PieChart
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Relatórios</h1>
          <p className="text-slate-600 mt-1">
            Gere relatórios detalhados da folha de pagamento
          </p>
        </div>
      </div>

      {/* Report type selection */}
      <Card>
        <CardHeader>
          <CardTitle>Tipo de Relatório</CardTitle>
          <CardDescription>
            Selecione o tipo de relatório que deseja gerar
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reports.map((report) => {
              const Icon = report.icon;
              const isActive = activeReport === report.key;
              
              return (
                <Card
                  key={report.key}
                  className={`cursor-pointer transition-all duration-200 card-hover ${
                    isActive ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                  }`}
                  onClick={() => setActiveReport(report.key)}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center space-x-3">
                      <Icon className={`h-8 w-8 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                      <div>
                        <h3 className="font-semibold text-slate-800">{report.title}</h3>
                        <p className="text-sm text-slate-600">{report.description}</p>
                      </div>
                      {isActive && (
                        <Badge className="ml-auto">Selecionado</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Filtros</span>
          </CardTitle>
          <CardDescription>
            Configure os filtros para personalizar seu relatório
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {activeReport === 'charts' && (
            <div className="text-center py-8">
              <PieChart className="h-16 w-16 mx-auto mb-4 text-blue-500" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Relatório de Gráficos</h3>
              <p className="text-slate-600 mb-4">
                Visualize os dados em formato de gráficos interativos
              </p>
              <p className="text-sm text-slate-500">
                Clique em "Gerar Relatório" para acessar a página de gráficos
              </p>
            </div>
          )}
          
          {activeReport === 'complete' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="empresa">Empresa</Label>
                <Select value={filters.empresa} onValueChange={(value) => setFilters(prev => ({ ...prev, empresa: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as empresas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas as empresas</SelectItem>
                    {options.companies.map((company) => (
                      <SelectItem key={company.id} value={company.name}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="nome">Nome do Funcionário</Label>
                <Input
                  id="nome"
                  value={filters.nome}
                  onChange={(e) => setFilters(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Digite o nome"
                />
              </div>

              <div>
                <Label htmlFor="filiais">Filial</Label>
                <Select value={filters.filiais} onValueChange={(value) => setFilters(prev => ({ ...prev, filiais: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as filiais" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas as filiais</SelectItem>
                    {options.branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.name}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="cargo">Cargo</Label>
                <Select value={filters.cargo} onValueChange={(value) => setFilters(prev => ({ ...prev, cargo: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os cargos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os cargos</SelectItem>
                    {options.positions.map((position) => (
                      <SelectItem key={position.id} value={position.name}>
                        {position.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="regime">Regime</Label>
                <Select value={filters.regime} onValueChange={(value) => setFilters(prev => ({ ...prev, regime: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os regimes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os regimes</SelectItem>
                    <SelectItem value="CLT">CLT</SelectItem>
                    <SelectItem value="PJ">PJ</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="situacao">Situação</Label>
                <Select value={filters.situacao} onValueChange={(value) => setFilters(prev => ({ ...prev, situacao: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as situações" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas as situações</SelectItem>
                    {options.status.map((status) => (
                      <SelectItem key={status.id} value={status.name}>
                        {status.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="admissao_inicio">Data Admissão - Início</Label>
                <Input
                  id="admissao_inicio"
                  type="date"
                  value={filters.admissao_inicio}
                  onChange={(e) => setFilters(prev => ({ ...prev, admissao_inicio: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="admissao_fim">Data Admissão - Fim</Label>
                <Input
                  id="admissao_fim"
                  type="date"
                  value={filters.admissao_fim}
                  onChange={(e) => setFilters(prev => ({ ...prev, admissao_fim: e.target.value }))}
                />
              </div>
            </div>
          )}

          {activeReport === 'company-totals' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="empresa_totals">Empresa</Label>
                <Select value={filters.empresa} onValueChange={(value) => setFilters(prev => ({ ...prev, empresa: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as empresas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas as empresas</SelectItem>
                    {options.companies.map((company) => (
                      <SelectItem key={company.id} value={company.name}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-4">
            <Button
              onClick={generateReport}
              disabled={loading}
              className="btn-primary flex items-center space-x-2"
            >
              {loading ? (
                <div className="loading-spinner w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : activeReport === 'charts' ? (
                <PieChart className="h-4 w-4" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              <span>{activeReport === 'charts' ? 'Acessar Gráficos' : 'Gerar Relatório'}</span>
            </Button>
            
            <Button
              variant="outline"
              onClick={clearFilters}
            >
              Limpar Filtros
            </Button>

            {reportData.length > 0 && (
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  onClick={handleExportCSV}
                  disabled={exporting}
                  className="flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>CSV</span>
                </Button>
                
                <Button
                  variant="outline"
                  onClick={handleExportExcel}
                  disabled={exporting}
                  className="flex items-center space-x-2"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  <span>Excel</span>
                </Button>
                
                <Button
                  variant="outline" 
                  onClick={handleExportPDF}
                  disabled={exporting}
                  className="flex items-center space-x-2"
                >
                  <FileImage className="h-4 w-4" />
                  <span>PDF</span>
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {reportData.length > 0 && (
        <Card>
          <CardHeader>
            {/* Company Header with Logo */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200">
              <div className="flex items-center space-x-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_052621d4-6d16-40ea-8523-d7c1082d0093/artifacts/87ni3ke8_Logo%20Place.svg"
                  alt="Place Logistics Logo"
                  className="h-10 w-auto"
                />
                <div>
                  <h2 className="text-lg font-bold text-slate-800">GridProfile</h2>
                  <p className="text-sm text-slate-600">Sistema de Folha de Pagamento</p>
                </div>
              </div>
              <div className="text-right text-sm text-slate-600">
                <p>Relatório gerado em:</p>
                <p className="font-medium">{new Date().toLocaleDateString('pt-BR')}</p>
              </div>
            </div>
            
            <CardTitle className="flex items-center justify-between">
              <span>Resultados do Relatório</span>
              <Badge variant="outline">
                {reportData.length} {reportData.length === 1 ? 'registro' : 'registros'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {activeReport === 'complete' && (
                      <>
                        <TableHead>Nome</TableHead>
                        <TableHead>Empresa</TableHead>
                        <TableHead>Filiais</TableHead>
                        <TableHead>Cargo</TableHead>
                        <TableHead>Admissão</TableHead>
                        <TableHead>Data de Demissão</TableHead>
                        <TableHead>Regime</TableHead>
                        <TableHead>Departamento</TableHead>
                        <TableHead>Situação</TableHead>
                        <TableHead className="text-right">Salário</TableHead>
                        <TableHead className="text-right">Acúmulo Função</TableHead>
                        <TableHead className="text-right">VT</TableHead>
                        <TableHead className="text-right">Desconto VT</TableHead>
                        <TableHead className="text-right">VT Custo</TableHead>
                        <TableHead className="text-right">VA</TableHead>
                        <TableHead className="text-right">VR</TableHead>
                        <TableHead className="text-right">Aux. Combustível</TableHead>
                        <TableHead className="text-right">Auxílio Creche</TableHead>
                        <TableHead className="text-right">2ª Refeição</TableHead>
                        <TableHead className="text-right">Pernoite</TableHead>
                        <TableHead className="text-right">Assist. Médica</TableHead>
                        <TableHead className="text-right">Odonto</TableHead>
                        <TableHead className="text-right">Seg. Vida</TableHead>
                        <TableHead className="text-right">Total Pass</TableHead>
                        <TableHead className="text-right">13º NF</TableHead>
                        <TableHead className="text-right">Med. Ocupacional</TableHead>
                        <TableHead className="text-right">Prov. 13º</TableHead>
                        <TableHead className="text-right">Prov. Férias</TableHead>
                        <TableHead className="text-right">FGTS</TableHead>
                        <TableHead className="text-right">INSS</TableHead>
                        <TableHead className="text-right">TOTAL</TableHead>
                      </>
                    )}
                    {activeReport === 'company-totals' && (
                      <>
                        <TableHead>Empresa</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </>
                    )}
                    {activeReport === 'position-salaries' && (
                      <>
                        <TableHead>Empresa</TableHead>
                        <TableHead>Cargo</TableHead>
                        <TableHead>Departamento</TableHead>
                        <TableHead>Modalidade</TableHead>
                        <TableHead className="text-right">Júnior I</TableHead>
                        <TableHead className="text-right">Júnior II</TableHead>
                        <TableHead className="text-right">Júnior III</TableHead>
                        <TableHead className="text-right">Pleno I</TableHead>
                        <TableHead className="text-right">Pleno II</TableHead>
                        <TableHead className="text-right">Pleno III</TableHead>
                        <TableHead className="text-right">Sênior I</TableHead>
                        <TableHead className="text-right">Sênior II</TableHead>
                        <TableHead className="text-right">Sênior III</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.map((row, index) => (
                    <TableRow key={index} className="table-row">
                      {activeReport === 'complete' && (
                        <>
                          <TableCell className="font-medium">{row.nome}</TableCell>
                          <TableCell>{row.empresa}</TableCell>
                          <TableCell>{row.filiais || '-'}</TableCell>
                          <TableCell>{row.cargo || '-'}</TableCell>
                          <TableCell>{formatDate(row.admissao)}</TableCell>
                          <TableCell>{row.data_demissao ? formatDate(row.data_demissao) : '-'}</TableCell>
                          <TableCell>
                            <Badge variant={row.regime === 'CLT' ? 'default' : 'outline'}>
                              {row.regime}
                            </Badge>
                          </TableCell>
                          <TableCell>{row.departamento || '-'}</TableCell>
                          <TableCell>{row.situacao || '-'}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.salario)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.acumulo_funcao)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.vt)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.desconto_vt)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.vt_custo)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.va)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.vr)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.aux_combustivel)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.auxilio_creche)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.segunda_refeicao)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.pernoite)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.assist_medica)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.odonto)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.seg_vida)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.total_pass)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.nf_13)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.med_ocupacional)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.prov_13)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.prov_ferias)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.fgts)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.inss)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {formatCurrency(row.total)}
                          </TableCell>
                        </>
                      )}
                      {activeReport === 'company-totals' && (
                        <>
                          <TableCell className="font-medium">{row.empresa}</TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {formatCurrency(row.total)}
                          </TableCell>
                        </>
                      )}
                      {activeReport === 'position-salaries' && (
                        <>
                          <TableCell className="font-medium">{row.empresa}</TableCell>
                          <TableCell>{row.cargo}</TableCell>
                          <TableCell>{row.departamento || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={row.modalidade === 'CLT' ? 'default' : 'outline'}>
                              {row.modalidade}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.salario_junior_i)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.salario_junior_ii)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.salario_junior_iii)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.salario_pleno_i)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.salario_pleno_ii)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.salario_pleno_iii)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.salario_senior_i)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.salario_senior_ii)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.salario_senior_iii)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsPage;